import { useFlowNavigation } from '../hooks/useFlowNavigation';
import {
  useClearScenePlanningFields,
  usePlanningState,
  useSetShowSettingsPopup,
  useShowSettingsPopup,
  useUndoRedo,
  useUpdatePlanningField,
} from '../store/hooks';
import { countWords } from '../utils/dynamicBoxStyles';

import { ConceptPreview } from './ConceptPreview';
import { SettingsButton } from './SettingsButton';
import { SettingsPopup } from './SettingsPopup';
import { UnifiedBottomBar } from './UnifiedBottomBar';
import { UnifiedTopBar } from './UnifiedTopBar';

export default function ScenePlanning() {
  const { goToNextStep, goToPreviousStep } = useFlowNavigation();
  const planningState = usePlanningState();
  const updatePlanningField = useUpdatePlanningField();
  const clearScenePlanningFields = useClearScenePlanningFields();
  const { undo, redo, canUndo, canRedo } = useUndoRedo();
  const showSettingsPopup = useShowSettingsPopup();
  const setShowSettingsPopup = useSetShowSettingsPopup();

  // Check completion status for each box - now just checking for any content
  const playerMotivationComplete = planningState.playerMotivation.trim().length > 0;
  const playerBoxComplete =
    planningState.playerRole.trim().length > 0 &&
    planningState.playerName.trim().length > 0 &&
    playerMotivationComplete;

  const npcMotivationComplete = planningState.npcMotivation.trim().length > 0;
  const npcBoxComplete =
    planningState.npcRole.trim().length > 0 &&
    planningState.npcName.trim().length > 0 &&
    npcMotivationComplete;

  const aPlotComplete = planningState.aPlot.trim().length > 0;
  const stakesComplete = planningState.stakes.trim().length > 0;
  const conflictBoxComplete = aPlotComplete && stakesComplete;

  const locationComplete = planningState.locationAtmosphere.trim().length > 0;

  const canContinue =
    playerBoxComplete && npcBoxComplete && conflictBoxComplete && locationComplete;

  // Determine which box is active based on completion
  const activeBoxIndex = !playerBoxComplete
    ? 1
    : !npcBoxComplete
      ? 2
      : !conflictBoxComplete
        ? 3
        : !locationComplete
          ? 4
          : 0;

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#050614] to-[#0f1a2f] flex flex-col">
      {/* Header */}
      <UnifiedTopBar
        subtitle="Scene Planning"
        stepInfo="Planner Stage 2 of 2"
        actions={<SettingsButton />}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-8 py-6">
          <div className="mb-6">
            <h2 className="text-3xl lg:text-4xl font-bold uppercase bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] bg-clip-text text-transparent">
              SCENE PLANNING
            </h2>
            <p className="text-gray-300 text-lg text-balance">
              You have chosen and thought through a dynamic -- now, you&apos;re going to add
              specificity and turn them into actual characters with goals.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            {/* Left Column: Form Fields */}
            <div className="lg:col-span-2 space-y-4">
              {/* Character Info Card Container */}
              <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border border-[#1a2d4d] rounded-xl p-6 shadow-lg">
                {/* Friction Text */}
                <div className="mb-4">
                  <p className="text-gray-300 text-sm leading-relaxed text-balance">
                    The friction between differing, sometimes opposing &apos;wants&apos; and needs
                    is what ignites a great scene -- both people are unable to simply walk away, but
                    compete for who is &apos;more getting what they want&apos; vs the other person.
                  </p>
                </div>

                {/* Character Roles & Names - Two Cards Side by Side */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Player Card */}
                  <div
                    className={`rounded-lg p-5 transition-all duration-300 border ${
                      playerBoxComplete
                        ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#10b981]/30 shadow-md shadow-[#10b981]/10'
                        : activeBoxIndex === 1
                          ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#4f46e5]/50 shadow-md shadow-[#4f46e5]/20'
                          : 'bg-[#0a0f1f]/50 border-[#1a2d4d]/50 hover:border-[#1a2d4d]/70'
                    }`}
                  >
                    <h3 className="text-base font-semibold mb-3 transition-colors duration-300">
                      <span
                        className={`${
                          playerBoxComplete
                            ? 'text-[#10b981]'
                            : activeBoxIndex === 1
                              ? 'bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] bg-clip-text text-transparent'
                              : 'text-gray-300'
                        }`}
                      >
                        4a. Player Character
                      </span>
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">
                          Player Role
                        </label>
                        <input
                          type="text"
                          value={planningState.playerRole}
                          onChange={(e) => updatePlanningField('playerRole', e.target.value)}
                          placeholder="e.g., grieving son, nervous employee..."
                          className="w-full bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">
                          Player Name
                        </label>
                        <input
                          type="text"
                          value={planningState.playerName}
                          onChange={(e) => updatePlanningField('playerName', e.target.value)}
                          placeholder="Enter name..."
                          className="w-full bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">
                          Player&apos;s Initial Motivation/Goal in this Scene:
                        </label>
                        <input
                          type="text"
                          value={planningState.playerMotivation}
                          onChange={(e) => updatePlanningField('playerMotivation', e.target.value)}
                          placeholder="e.g., to resist the NPC, to understand the situation, to achieve a specific outcome, to express an emotion..."
                          className="w-full bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent"
                        />
                        <div className="text-right text-xs mt-1 hidden">
                          <span
                            className={playerMotivationComplete ? 'text-gray-500' : 'text-red-500'}
                          >
                            {countWords(planningState.playerMotivation)} / 15 words
                          </span>
                        </div>
                      </div>
                    </div>
                    {playerBoxComplete && (
                      <div className="text-right text-xs text-[#10b981] mt-2">
                        ✓ Player details complete
                      </div>
                    )}
                  </div>

                  {/* NPC Card */}
                  <div
                    className={`rounded-lg p-5 transition-all duration-300 border ${
                      npcBoxComplete
                        ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#10b981]/30 shadow-md shadow-[#10b981]/10'
                        : activeBoxIndex === 2
                          ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#4f46e5]/50 shadow-md shadow-[#4f46e5]/20'
                          : 'bg-[#0a0f1f]/50 border-[#1a2d4d]/50 hover:border-[#1a2d4d]/70'
                    }`}
                  >
                    <h3 className="text-base font-semibold mb-3 transition-colors duration-300">
                      <span
                        className={`${
                          npcBoxComplete
                            ? 'text-[#10b981]'
                            : activeBoxIndex === 2
                              ? 'bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] bg-clip-text text-transparent'
                              : 'text-gray-300'
                        }`}
                      >
                        4b. Non-Player Character
                      </span>
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">
                          NPC Role
                        </label>
                        <input
                          type="text"
                          value={planningState.npcRole}
                          onChange={(e) => updatePlanningField('npcRole', e.target.value)}
                          placeholder="e.g., widowed father, demanding boss..."
                          className="w-full bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">
                          NPC Name
                        </label>
                        <input
                          type="text"
                          value={planningState.npcName}
                          onChange={(e) => updatePlanningField('npcName', e.target.value)}
                          placeholder="Enter name..."
                          className="w-full bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">
                          NPC&apos;s Core Motivation/Goal in this Scene:
                        </label>
                        <input
                          type="text"
                          value={planningState.npcMotivation}
                          onChange={(e) => updatePlanningField('npcMotivation', e.target.value)}
                          placeholder="e.g., to get the player to comply, to seek forgiveness, to assert dominance, to share a burden..."
                          className="w-full bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent"
                        />
                        <div className="text-right text-xs mt-1 hidden">
                          <span
                            className={npcMotivationComplete ? 'text-gray-500' : 'text-red-500'}
                          >
                            {countWords(planningState.npcMotivation)} / 15 words
                          </span>
                        </div>
                      </div>
                    </div>
                    {npcBoxComplete && (
                      <div className="text-right text-xs text-[#10b981] mt-2">
                        ✓ NPC details complete
                      </div>
                    )}
                  </div>
                </div>
                {/* End Character Info Card Container */}
              </div>

              {/* Conflict & Stakes Info Card Container */}
              <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border border-[#1a2d4d] rounded-xl p-6 shadow-lg">
                <div className="grid grid-cols-2 gap-4 items-start">
                  {/* Left side: Explanatory Text (50%) */}
                  <div className="col-span-1">
                    <div className="pr-2 space-y-4">
                      <div>
                        <h5 className="text-gray-200 font-semibold text-base mb-2">The A-Plot</h5>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          If you asked either person why they&apos;re talking, this is what
                          they&apos;d agree on as the reason for their talking like this. The
                          &apos;A-Plot&apos; is the central issue, conflict, or transaction they
                          must address or resolve.
                        </p>
                      </div>
                      <div>
                        <h5 className="text-gray-200 font-semibold text-base mb-2">The Stakes</h5>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          These can be more subconscious or hard to admit. Beyond tangible gains or
                          losses (like money, respect, or love) from the conversation&apos;s
                          outcome, stakes involve how characters feel about themselves – their
                          psychological state and underlying needs. It&apos;s what they truly stand
                          to gain or lose based on how the &apos;A-Plot&apos; unfolds.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Right side: Conflict & Stakes Box (50%) */}
                  <div className="col-span-1">
                    <div
                      className={`rounded-lg p-5 transition-all duration-300 border ${
                        conflictBoxComplete
                          ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#10b981]/30 shadow-md shadow-[#10b981]/10'
                          : activeBoxIndex === 3
                            ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#4f46e5]/50 shadow-md shadow-[#4f46e5]/20'
                            : 'bg-[#0a0f1f]/50 border-[#1a2d4d]/50 hover:border-[#1a2d4d]/70'
                      }`}
                    >
                      <h3 className="text-base font-semibold mb-3 transition-colors duration-300">
                        <span
                          className={`${
                            conflictBoxComplete
                              ? 'text-[#10b981]'
                              : activeBoxIndex === 3
                                ? 'bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] bg-clip-text text-transparent'
                                : 'text-gray-300'
                          }`}
                        >
                          4c. Conflict and Stakes
                        </span>
                      </h3>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            The A-Plot: What is the central issue, conflict, or transaction that
                            these characters *must* address or resolve in this conversation?
                          </label>
                          <textarea
                            value={planningState.aPlot}
                            onChange={(e) => updatePlanningField('aPlot', e.target.value)}
                            placeholder="e.g., a broken promise, a demand for information, a confession, a business deal gone wrong."
                            className="w-full bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-3 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] transition-all duration-200 resize-none"
                            rows={3}
                          />
                          <div className="text-right text-xs mt-1 hidden">
                            <span className={aPlotComplete ? 'text-gray-500' : 'text-red-500'}>
                              {countWords(planningState.aPlot)} / 15 words
                            </span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            The Stakes: What does each character stand to gain or lose based on the
                            outcome of this A-Plot discussion?
                          </label>
                          <textarea
                            value={planningState.stakes}
                            onChange={(e) => updatePlanningField('stakes', e.target.value)}
                            placeholder="e.g., reputation, a relationship, money, safety, self-respect."
                            className="w-full bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-3 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] transition-all duration-200 resize-none"
                            rows={3}
                          />
                          <div className="text-right text-xs mt-1 hidden">
                            <span className={stakesComplete ? 'text-gray-500' : 'text-red-500'}>
                              {countWords(planningState.stakes)} / 15 words
                            </span>
                          </div>
                        </div>
                      </div>
                      {conflictBoxComplete && (
                        <div className="text-right text-xs text-[#10b981] mt-2">
                          ✓ Conflict details complete
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Live Concept Preview */}
            <div className="lg:sticky lg:top-4 lg:h-fit space-y-4">
              <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border border-[#1a2d4d] rounded-xl p-6 shadow-lg">
                <h3 className="text-base font-semibold text-white mb-2 flex items-center">
                  <span className="w-2 h-2 bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] rounded-full mr-2"></span>
                  Scene Concept{' '}
                  <span className="text-gray-400 font-normal ml-2">(Live Update)</span>
                </h3>
                <p className="text-gray-400 text-xs mb-3">
                  You may need to reword your answers in the other fields to make them fit the flow
                </p>

                <ConceptPreview planningState={planningState} />

                {/* Color Legend */}
                <div className="bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-4 mt-4 text-xs">
                  <h4 className="text-gray-400 font-medium mb-2">Color Guide:</h4>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    <div className="flex items-center gap-1">
                      <span className="text-cyan-400">●</span>
                      <span className="text-gray-400">Player Role/Name</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-emerald-400">●</span>
                      <span className="text-gray-400">NPC Role/Name</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-blue-400">●</span>
                      <span className="text-gray-400">Player Motivation</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-green-400">●</span>
                      <span className="text-gray-400">NPC Motivation</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-yellow-400">●</span>
                      <span className="text-gray-400">A-Plot</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-orange-400">●</span>
                      <span className="text-gray-400">Stakes</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-purple-400">●</span>
                      <span className="text-gray-400">Location</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-pink-400">●</span>
                      <span className="text-gray-400">B-Plot Seed</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Box */}
              <div
                className={`rounded-xl p-6 transition-all duration-300 border ${
                  locationComplete
                    ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#10b981]/30 shadow-lg shadow-[#10b981]/10'
                    : activeBoxIndex === 4
                      ? 'bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-[#4f46e5]/50 shadow-lg shadow-[#4f46e5]/20'
                      : 'bg-gradient-to-br from-[#0c1222]/50 to-[#0a1020]/50 border-[#1a2d4d] hover:border-[#1a2d4d]/70'
                }`}
              >
                <h3 className="text-base font-semibold mb-3 transition-colors duration-300">
                  <span
                    className={`${
                      locationComplete
                        ? 'text-[#10b981]'
                        : activeBoxIndex === 4
                          ? 'bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] bg-clip-text text-transparent'
                          : 'text-gray-300'
                    }`}
                  >
                    5. Location of this Conversation
                  </span>
                </h3>

                <div>
                  <textarea
                    value={planningState.locationAtmosphere}
                    onChange={(e) => updatePlanningField('locationAtmosphere', e.target.value)}
                    placeholder="e.g., a tense family kitchen after an argument, a sterile corporate boardroom during a difficult negotiation, a quiet café where a difficult truth is about to be revealed."
                    className="w-full bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-3 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] transition-all duration-200 resize-none"
                    rows={6}
                  />
                </div>
                {locationComplete && (
                  <div className="text-right text-xs text-[#10b981] mt-2">✓ Location complete</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Planner Navigation */}
      <UnifiedBottomBar
        variant="planner"
        onBack={goToPreviousStep}
        onContinue={goToNextStep}
        canContinue={canContinue}
        continueLabel="Create the Game ->"
        onUndo={undo}
        onRedo={redo}
        onClear={clearScenePlanningFields}
        canUndo={canUndo}
        canRedo={canRedo}
      />
      <SettingsPopup isOpen={showSettingsPopup} onClose={() => setShowSettingsPopup(false)} />
    </div>
  );
}
